import { httpsCallable } from 'firebase/functions';
import { 
  collection, 
  query, 
  where, 
  getCountFromServer, 
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { functions, db } from './firebase';
import { sendCustomerMessage, CustomerMessageData } from './customerService';

export interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  content: string;
  type: 'newsletter' | 'promotional';
  status: 'sent';
  recipients: number;
  sentDate: Date;
  createdAt: Date;
  updatedAt: Date;
  template?: string;
  plainTextOnly?: boolean;
}

/**
 * Email Campaign Service
 * Handles email campaign CRUD operations and sending using existing customer message infrastructure
 */
export class EmailCampaignService {
  
  /**
   * Get email recipients based on campaign type
   */
  private static async getEmailRecipients(type: 'newsletter' | 'promotional'): Promise<{
    email: string;
    name: string;
    customerId?: string;
  }[]> {
    try {
      let recipients: { email: string; name: string; customerId?: string }[] = [];

      if (type === 'newsletter') {
        // Get all newsletter subscribers
        const newsletterRef = collection(db, 'newsletters');
        const newsletterSnapshot = await getDocs(newsletterRef);
        
        newsletterSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.isActive) {
            recipients.push({
              email: data.email,
              name: data.email.split('@')[0], // Use email prefix as name fallback
            });
          }
        });
        
      } else if (type === 'promotional') {
        // Get customers who have promotions enabled in their notification preferences
        const usersRef = collection(db, 'users');
        const promotionalQuery = query(
          usersRef, 
          where('notificationPreferences.promotions', '==', true)
        );
        const usersSnapshot = await getDocs(promotionalQuery);
        
        usersSnapshot.forEach((doc) => {
          const data = doc.data();
          recipients.push({
            email: data.email,
            name: data.name || data.email.split('@')[0],
            customerId: doc.id,
          });
        });
      }

      console.log(`📧 Found ${recipients.length} recipients for ${type} campaign`);
      return recipients;
      
    } catch (error: any) {
      console.error('Error getting email recipients:', error);
      return [];
    }
  }

  /**
   * Send batch emails with rate limiting (1.5 requests per second)
   */
  private static async sendBatchEmails(
    recipients: { email: string; name: string; customerId?: string }[],
    subject: string,
    content: string,
    campaignName: string,
    template?: string,
    plainTextOnly?: boolean
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    const results = { success: 0, failed: 0, errors: [] as string[] };
    const delay = 667; // 667ms delay = 1.5 requests per second
    
    console.log(`📬 Starting batch email send to ${recipients.length} recipients with ${delay}ms delay between sends`);
    
    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];
      
              try {
          const messageData: CustomerMessageData = {
            customerId: recipient.customerId || 'newsletter-subscriber',
            customerEmail: recipient.email,
            customerName: recipient.name,
            subject: subject,
            content: content,
            template: template || 'email-campaign',
            category: 'Email Campaign',
            plainTextOnly
          };

        await sendCustomerMessage(messageData);
        results.success++;
        
        // Log progress for every 10 emails or at the end
        if ((i + 1) % 10 === 0 || i === recipients.length - 1) {
          console.log(`📧 Progress: ${i + 1}/${recipients.length} emails sent (${results.success} successful, ${results.failed} failed)`);
        }
        
      } catch (error: any) {
        results.failed++;
        const errorMsg = `Failed to send to ${recipient.email}: ${error.message}`;
        results.errors.push(errorMsg);
        console.error(errorMsg);
      }
      
      // Add delay between requests (except for the last one)
      if (i < recipients.length - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    console.log(`✅ Batch email send completed: ${results.success} successful, ${results.failed} failed`);
    return results;
  }

  /**
   * Create a new email campaign
   */
  static async createEmailCampaign(campaignData: Omit<EmailCampaign, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const campaignRef = collection(db, 'email_campaigns');
      const docRef = await addDoc(campaignRef, {
        ...campaignData,
        plainTextOnly: campaignData.plainTextOnly ?? false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        sentDate: Timestamp.fromDate(campaignData.sentDate),
      });
      
      console.log('Email campaign created:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('Error creating email campaign:', error);
      throw new Error('Failed to create email campaign');
    }
  }

  /**
   * Update an existing email campaign
   */
  static async updateEmailCampaign(campaignId: string, updates: Partial<Omit<EmailCampaign, 'id' | 'createdAt'>>): Promise<void> {
    try {
      const campaignRef = doc(db, 'email_campaigns', campaignId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
      };
      
      if (updates.sentDate) {
        updateData.sentDate = Timestamp.fromDate(updates.sentDate);
      }
      
      await updateDoc(campaignRef, updateData);
      console.log('Email campaign updated:', campaignId);
    } catch (error: any) {
      console.error('Error updating email campaign:', error);
      throw new Error('Failed to update email campaign');
    }
  }

  /**
   * Delete an email campaign
   */
  static async deleteEmailCampaign(campaignId: string): Promise<void> {
    try {
      const campaignRef = doc(db, 'email_campaigns', campaignId);
      await deleteDoc(campaignRef);
      console.log('Email campaign deleted:', campaignId);
    } catch (error: any) {
      console.error('Error deleting email campaign:', error);
      throw new Error('Failed to delete email campaign');
    }
  }

  /**
   * Send email campaign immediately using existing customer message infrastructure
   */
  static async sendEmailCampaign(campaignData: {
    name: string;
    subject: string;
    content: string;
    type: 'newsletter' | 'promotional';
    template?: string;
    plainTextOnly?: boolean;
  }): Promise<{ success: boolean; message: string; campaignId?: string }> {
    try {
      console.log('Sending email campaign:', campaignData);
      
      // Get recipients based on campaign type
      const recipients = await this.getEmailRecipients(campaignData.type);
      
      if (recipients.length === 0) {
        return {
          success: false,
          message: `No recipients found for ${campaignData.type} campaign. Please check if subscribers have opted in for this type of communication.`
        };
      }

      // Calculate estimated time and warn for large campaigns
      const requestsPerSecond = 1.5;
      const estimatedTimeMinutes = Math.ceil(recipients.length / requestsPerSecond / 60);
      
      // Log info for large campaigns
      if (recipients.length > 100) {
        console.log(`📊 Large campaign: ${recipients.length} recipients, estimated time: ${estimatedTimeMinutes} minutes`);
      }
      
      // Create campaign record in Firebase
      const campaignRecord: Omit<EmailCampaign, 'id' | 'createdAt' | 'updatedAt'> = {
        name: campaignData.name,
        subject: campaignData.subject,
        content: campaignData.content,
        type: campaignData.type,
        status: 'sent',
        recipients: recipients.length,
        sentDate: new Date(),
        template: campaignData.template,
        plainTextOnly: !!campaignData.plainTextOnly
      };
      
      const campaignId = await this.createEmailCampaign(campaignRecord);
      
      // Send batch emails
      const results = await this.sendBatchEmails(
        recipients,
        campaignData.subject,
        campaignData.content,
        campaignData.name,
        campaignData.template,
        campaignData.plainTextOnly
      );
      
      // Check results and return appropriate response
      const totalRecipients = results.success + results.failed;
      const successRate = results.success / totalRecipients;
      
      if (results.success === 0) {
        return {
          success: false,
          message: `Campaign failed to send to all ${totalRecipients} recipients. First error: ${results.errors[0] || 'Unknown error'}`
        };
      }
      
      if (successRate >= 0.9) {
        // 90% or more success rate
        const timeInfo = estimatedTimeMinutes > 1 ? ` (completed in ~${estimatedTimeMinutes} minutes)` : '';
        return {
          success: true,
          message: `Campaign sent successfully to ${results.success} of ${totalRecipients} recipients!${results.failed > 0 ? ` (${results.failed} failed)` : ''}${timeInfo}`,
          campaignId: campaignId
        };
      } else {
        // Less than 90% success rate
        return {
          success: false,
          message: `Campaign partially failed: ${results.success} sent, ${results.failed} failed out of ${totalRecipients} recipients. First error: ${results.errors[0] || 'Unknown error'}`
        };
      }
      
    } catch (error: any) {
      console.error('Error sending email campaign:', error);
      
      return {
        success: false,
        message: error.message || 'Failed to send email campaign'
      };
    }
  }

  /**
   * Get email campaign recipients count using Firebase aggregate functions
   */
  static async getEmailCampaignRecipientsCount(type: 'newsletter' | 'promotional'): Promise<{ 
    success: boolean; 
    count: number; 
    message?: string 
  }> {
    try {
      let count = 0;

      if (type === 'newsletter') {
        // Count all newsletter subscribers
        const newsletterRef = collection(db, 'newsletters');
        const snapshot = await getCountFromServer(newsletterRef);
        count = snapshot.data().count;
        
      } else if (type === 'promotional') {
        // Count customers who have promotions enabled in their notification preferences
        const usersRef = collection(db, 'users');
        const promotionalQuery = query(
          usersRef, 
          where('notificationPreferences.promotions', '==', true)
        );
        const snapshot = await getCountFromServer(promotionalQuery);
        count = snapshot.data().count;
      }

      console.log(`📊 Email campaign recipients count for ${type}: ${count}`);

      return {
        success: true,
        count: count,
        message: `Found ${count} ${type} subscribers`
      };
      
    } catch (error: any) {
      console.error('Error getting recipients count:', error);
      
      return {
        success: false,
        count: 0,
        message: error.message || 'Failed to get recipients count'
      };
    }
  }

  /**
   * Subscribe to email campaigns real-time updates
   */
  static subscribeToEmailCampaigns(callback: (campaigns: EmailCampaign[]) => void): () => void {
    const campaignsRef = collection(db, 'email_campaigns');
    const campaignsQuery = query(campaignsRef, orderBy('createdAt', 'desc'));
    
    return onSnapshot(campaignsQuery, (snapshot) => {
      const campaigns: EmailCampaign[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        campaigns.push({
          id: doc.id,
          name: data.name || '',
          subject: data.subject || '',
          content: data.content || '',
          type: data.type || 'newsletter',
          status: 'sent',
          recipients: data.recipients || 0,
          sentDate: data.sentDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          template: data.template,
          plainTextOnly: data.plainTextOnly || false,
        });
      });
      
      callback(campaigns);
    }, (error) => {
      console.error('Error subscribing to email campaigns:', error);
      callback([]);
    });
  }

  /**
   * Get all email campaigns
   */
  static async getEmailCampaigns(): Promise<EmailCampaign[]> {
    try {
      const campaignsRef = collection(db, 'email_campaigns');
      const campaignsQuery = query(campaignsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(campaignsQuery);
      
      const campaigns: EmailCampaign[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        campaigns.push({
          id: doc.id,
          name: data.name || '',
          subject: data.subject || '',
          content: data.content || '',
          type: data.type || 'newsletter',
          status: 'sent',
          recipients: data.recipients || 0,
          sentDate: data.sentDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          template: data.template,
          plainTextOnly: data.plainTextOnly || false,
        });
      });
      
      return campaigns;
    } catch (error: any) {
      console.error('Error getting email campaigns:', error);
      return [];
    }
  }
} 
